export const state = () => ({
    conversations: null,
    conversation: null,
    messages: null,
    has_unread_messages: false,
    search: null,
    filters: null,
    page: null,
    scroll_position: 0,
    bulk_message: false,
    selected_conversations: [],
});

export const mutations = {
    setConversations(state: any, payload: any) {
        state.conversations = payload;
    },

    setConversation(state: any, payload: any) {
        state.conversation = payload;
    },

    setMessages(state: any, payload: any) {
        state.messages = payload;
    },

    setHasUnreadMessages(state: any, payload: boolean) {
        state.has_unread_messages = payload;
    },

    setFilters(state: any, payload: any) {
        state.filters = payload;
    },

    setPage(state: any, payload: any) {
        state.page = payload;
    },

    setSearch(state: any, payload: string | null) {
        state.search = payload;
    },

    setScrollPosition(state: any, payload: any) {
        state.scroll_position = payload;
    },

    setBulkMessage(state: any, payload: any) {
        state.bulk_message = payload;
    },

    setSelectedConversations(state: any, payload: any) {
        state.selected_conversations = payload;
    },
};

export const getters = {
    getConversations: (state: any) => state.conversations,

    getConversation: (state: any) => state.conversation,

    getMessages: (state: any) => state.messages,

    getHasUnreadMessages: (state: any) => state.has_unread_messages,

    getFilters: (state: any) => state.filters,

    getSearch: (state: any) => state.search,

    getPage: (state: any) => state.page,

    getScrollPosition: (state: any) => state.scroll_position,

    getBulkMessage: (state: any) => state.bulk_message,

    getSelectedConversations: (state: any) => state.selected_conversations,
};

export const actions = {
    getConversations(state: any, payload: any) {
        const company_uuid = state.rootState.auth.user.company.uuid;

        let params = {
            page: state.state.page,
            search: state.state.search,
            ...state.state.filters,
        };

        Object.keys(params).forEach((key) => {
            if (!params[key]) delete params[key];
        });

        return new Promise((resolve, reject) => {
            this.$axios
                .get(`/api/company/${company_uuid}/conversations`, { params })
                .then((res) => {
                    let conversations;

                    if (state.state.conversations) {
                        conversations = JSON.parse(JSON.stringify(state.state.conversations));
                    }

                    if (params.page) {
                        conversations.data.push(...res.data.data);
                        conversations.links = res.data.links;
                        conversations.meta = res.data.meta;
                    } else {
                        conversations = res.data;
                    }

                    state.commit('setConversations', conversations);
                    state.commit('setHasUnreadMessages', conversations.meta.has_unread_messages || false);
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    getConversation(state: any, conversation_uuid: string) {
        return new Promise((resolve, reject) => {
            this.$axios
                .get(`/api/conversations/${conversation_uuid}`, {
                    params: { read: 1 },
                })
                .then((res) => {
                    const conversation = res.data;

                    state.dispatch('markConversationAsRead', conversation_uuid);
                    state.commit('setConversation', conversation);
                    state.commit('setMessages', conversation.messages);
                    resolve(conversation);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    createConversation(state: any, payload: any) {
        const company_uuid = state.rootState.auth.user.company.uuid;

        return new Promise((resolve, reject) => {
            this.$axios
                .post(`/api/company/${company_uuid}/conversations`, payload)
                .then((res) => {
                    const newConversation = res.data;
                    let conversations = JSON.parse(JSON.stringify(state.state.conversations));

                    if (conversations) {
                        conversations.data.push(newConversation);
                    }

                    // Set the filters if the user wants to see a conversation for an inactive job.
                    if (new Date(newConversation.job?.expires) <= new Date()) {
                        state.commit('setFilters', {
                            search: newConversation.candidate_name,
                            active: '0',
                        });
                    }

                    state.commit('setConversations', conversations);
                    state.commit('setConversation', newConversation);
                    resolve(newConversation);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    appendConversation(state: any, payload: any) {
        const { conversation_uuid, ...params } = payload;

        return new Promise((resolve, reject) => {
            this.$axios
                .get(`/api/conversations/${conversation_uuid}`, { params })
                .then((res) => {
                    let conversations = JSON.parse(JSON.stringify(state.state.conversations)) ?? [];

                    conversations.data = conversations.data.filter((item) => item.uuid !== conversation_uuid);

                    conversations.data.push({ ...res.data, has_unread_messages: true });

                    state.commit('setConversations', conversations);
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    appendMessage(state: any, message_uuid: string) {
        return new Promise((resolve, reject) => {
            this.$axios
                .get(`/api/messages/${message_uuid}`)
                .then((res) => {
                    const newMessage = res.data;

                    if (state.state.conversation.uuid !== newMessage.conversation.uuid) {
                        return resolve(newMessage);
                    }

                    let messages = JSON.parse(JSON.stringify(state.state.messages));

                    messages.push(newMessage);

                    state.dispatch('updateConversationTimestamp', newMessage.conversation.uuid);
                    state.commit('setMessages', messages);
                    resolve(newMessage);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    sendMessage(state: any, payload: any) {
        const { conversation_uuid, data } = payload;

        return new Promise((resolve, reject) => {
            this.$axios
                .post(`/api/conversations/${conversation_uuid}/messages`, data)
                .then((res) => {
                    const newMessage = res.data;

                    let messages = JSON.parse(JSON.stringify(state.state.messages)) || [];

                    messages.push(newMessage);

                    state.dispatch('updateConversationTimestamp', conversation_uuid);
                    state.commit('setMessages', messages);
                    resolve(newMessage);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    sendBulkMessages(state: any, payload: any) {
        const { conversation_uuids, data } = payload;

        return new Promise((resolve, reject) => {
            this.$axios
                .post(`/api/messages/send-bulk`, data)
                .then((res) => {
                    let messages = JSON.parse(JSON.stringify(state.state.messages)) || [];

                    messages.concat(res.data);

                    conversation_uuids.forEach((conversationUuid: string) => {
                        state.dispatch('updateConversationTimestamp', conversationUuid);
                    });

                    state.commit('setMessages', messages);
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    archiveConversation(state: any, conversation_uuid: string) {
        return new Promise((resolve, reject) => {
            this.$axios
                .put(`/api/conversations/${conversation_uuid}/archive-status`)
                .then((res) => {
                    let conversations = JSON.parse(JSON.stringify(state.state.conversations));
                    let conversation = JSON.parse(JSON.stringify(state.state.conversation));

                    // Remove the conversation from the list of conversations.
                    if (conversations) {
                        conversations.data = conversations.data.filter((conversation) => conversation.uuid !== res.data.uuid);
                    }

                    // Update the opened conversation's archived status.
                    if (conversation.uuid === res.data.uuid) {
                        conversation.archived_at = res.data.archived_at;
                    }

                    state.commit('setConversations', conversations);
                    state.commit('setConversation', conversation);
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    },

    setHasUnreadMessages(state: any, payload: boolean) {
        state.commit('setHasUnreadMessages', payload);
    },

    setFilters(state: any, payload: any) {
        state.commit('setFilters', payload);
    },

    setPage(state: any, payload: any) {
        state.commit('setPage', payload);
    },

    setSearch(state: any, payload: string | null) {
        state.commit('setSearch', payload);
        state.commit('setPage', null);
    },

    setScrollPosition(state: any, payload: any) {
        state.commit('setScrollPosition', payload);
    },

    setBulkMessage(state: any, payload: any) {
        state.commit('setBulkMessage', payload);
    },

    setSelectedConversations(state: any, payload: any) {
        state.commit('setSelectedConversations', payload);
    },

    markConversationAsRead(state: any, conversation_uuid: string) {
        let conversations = JSON.parse(JSON.stringify(state.state.conversations));

        conversations.data = conversations.data.map((conversation) => {
            if (conversation.uuid === conversation_uuid) {
                conversation.has_unread_messages = false;
            }
            return conversation;
        });

        if (!conversations.data.filter((c) => c.has_unread_messages).length) {
            state.commit('setHasUnreadMessages', false);
        }

        state.commit('setConversations', conversations);
    },

    markEmployerMessagesAsRead(state: any, conversation_uuid: string) {
        if (state.state.conversation?.uuid !== conversation_uuid) return;

        let messages = JSON.parse(JSON.stringify(state.state.messages));

        messages = messages.map((message) => {
            if (message.sender_type === 'Employer' && !message.read_at) {
                message.read_at = new Date();
            }
            return message;
        });

        state.commit('setMessages', messages);
    },

    updateConversationTimestamp(state: any, conversation_uuid: string) {
        if (!state.state.conversations) return;

        let conversations = JSON.parse(JSON.stringify(state.state.conversations));

        conversations.data = conversations.data.map((conversation) => {
            if (conversation.uuid === conversation_uuid) {
                conversation.updated_at = new Date();
            }
            return conversation;
        });

        state.commit('setConversations', conversations);
    },
};
